import { Injectable } from '@angular/core';
import { UserManager, UserManagerSettings, User } from 'oidc-client-ts';
import { BehaviorSubject, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { isClaim, Unity4Claim, UserClaimsVm } from '../models';
import { Router } from '@angular/router';
import { LoggerService } from '../../logger/services';
import { HttpClient } from '@angular/common/http';
import { EnvironmentVariables } from '../../environment-variables';
import { ToastrService } from 'ngx-toastr';
import { shareReplayRefCount } from '../../utils';
import { NgxDeepEqualsPureService } from 'ngx-deep-equals-pure';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private manager: UserManager;
  private user = new BehaviorSubject<User | undefined>(undefined);
  private user$ = this.user.pipe(
    distinctUntilChanged((prev, curr) =>
      this.deepEqualsSvc.deepEquals(prev, curr)
    ),
    shareReplayRefCount()
  );
  private usersApi = `${this.environment.apiPath}/users`;

  public get accessToken(): string | undefined {
    return this.user.getValue()?.access_token;
  }
  accessToken$ = this.user$.pipe(
    map((user) => user?.access_token),
    distinctUntilChanged(),
    shareReplayRefCount()
  );
  claims$ = this.user$.pipe(
    filter((user) => !!user),
    distinctUntilChanged(
      (prev, curr) => prev?.profile?.sub === curr?.profile?.sub
    ),
    switchMap(() => this.http.get<UserClaimsVm>(`${this.usersApi}/claims`)),
    map((vm) =>
      vm.claims
        .filter((claim) => isClaim(claim))
        .map((claim) => claim as Unity4Claim)
    ),
    shareReplayRefCount()
  );
  isLoggedIn$ = this.user$.pipe(
    map((user) => (user && !user.expired) || false),
    distinctUntilChanged(),
    shareReplayRefCount()
  );
  userId$ = this.user$.pipe(
    map((user) => (user?.profile.sub ? +user?.profile.sub : undefined)),
    distinctUntilChanged(),
    shareReplayRefCount()
  );

  constructor(
    private router: Router,
    private logger: LoggerService,
    private http: HttpClient,
    private environment: EnvironmentVariables,
    private toastr: ToastrService,
    private deepEqualsSvc: NgxDeepEqualsPureService
  ) {
    const config: UserManagerSettings = {
      authority: environment.authServer,
      client_id: environment.authClientId,
      redirect_uri: `${environment.basePath}/callback`,
      silent_redirect_uri: `${environment.basePath}/silent-renew`,
      silentRequestTimeoutInSeconds: 30, //30sec
      response_type: 'code',
      scope: `openid profile ${this.environment.authScope.join(' ')}`,
      post_logout_redirect_uri: `${environment.basePath}/`,
    };
    this.manager = new UserManager(config);
    this.setupOidcListeners();
    this.manager.getUser();
    this.userId$.subscribe((userId) => {
      this.logger.setUserId(userId);
    });
  }

  private allowRedirect(url: string) {
    return (
      this.environment.authRedirect === true ||
      (Array.isArray(this.environment.authRedirect) &&
        this.environment.authRedirect.includes(url.split('?')[0]))
    );
  }

  private setupOidcListeners(): void {
    this.manager.events.addUserLoaded((user) => {
      this.logger.warn('AuthService', 'UserLoaded', user)();
      if (user) {
        this.user.next(user);
      }
    });
    this.manager.events.addUserUnloaded(() => {
      this.logger.warn('AuthService', 'UserUnloaded')();
      this.user.next(undefined);
    });
    this.manager.events.addAccessTokenExpiring((...ev) => {
      this.logger.warn('AuthService', 'AccessTokenExpiring', ev)();
      //this may not be needed and the manager should be renewing itself...?
      /*
      this.manager.signinSilent().catch((err) => {
        this.logger.error('AuthService', 'signinSilent.catch()', { err })();
        this.toastr.error(
          'There was en error renewing your credentials.<br />Please logout and back in again when possible.',
          'Authorisation',
          { disableTimeOut: true, enableHtml: true }
        );
      });
      */
    });
    this.manager.events.addAccessTokenExpired((...ev) => {
      this.logger.warn('AuthService', 'AccessTokenExpired', ev)();
    });
    this.manager.events.addSilentRenewError((err) => {
      this.logger.error('AuthService', 'addSilentRenewError()', { err })();
      this.toastr.error(
        'There was en error renewing your credentials.<br />Please logout and back in again when possible.',
        'Authorisation',
        { disableTimeOut: true, enableHtml: true, closeButton: true }
      );
    });
  }

  completeAuthentication(): Promise<void> {
    return this.manager
      .signinCallback()
      .then((user) => {
        this.logger.info('AuthService', 'completeAuthentication', user)();
        // this.user.next(user);
        if (user && user.state && this.allowRedirect(user.state as string)) {
          this.router.navigateByUrl(user.state as string);
        } else {
          this.router.navigate(['/']);
        }
      })
      .catch((reason) => {
        this.logger.error(
          'AuthService',
          'completeAuthentication signinRedirectCallback',
          reason
        )();
        this.router.navigate(['/']);
      });
  }

  completeSilentAuthentication(): void {
    this.manager
      .signinCallback()
      .then((user) =>
        this.logger.info('AuthService', 'completeSilentAuthentication', user)()
      )
      .catch((e) =>
        this.logger.error('AuthService', 'completeSilentAuthentication', e)()
      );
  }

  hasClaim$(claim: Unity4Claim | Unity4Claim[]) {
    const _claims: Unity4Claim[] = Array.isArray(claim) ? [...claim] : [claim];
    return this.claims$.pipe(
      map((claims) => _claims.some((c) => claims.includes(c)))
    );
  }

  signOut(): Promise<void> {
    return this.manager.signoutRedirect();
  }

  startAuthentication(url: string) {
    this.logger.info('AuthService', 'startAuthentication', url)();
    // return this.manager.signinRedirect({ data: url });
    return this.manager
      .signinSilent({ state: url })
      .then((user) => {
        this.logger.info('AuthService', 'startAuthentication', user)();
        if (user && user.state && this.allowRedirect(user.state as string)) {
          this.router.navigateByUrl(user.state as string);
        } else {
          this.router.navigate(['/']);
        }
      })
      .catch((reason) => {
        this.logger.error(
          'AuthService',
          'startAuthentication signinSilent',
          reason
        )();
        // this.router.navigate(['/']);
        return this.manager.signinRedirect({ state: url });
      });
  }
}
